import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, FolderKanban, Users, Activity, Paperclip, Building2, Gem, Pencil, Check, X, MessagesSquare } from "lucide-react";
import ClientPortalDialog from "@/components/Client/ClientPortalDialog";
import AuditFooter from "@/components/AuditFooter";
import AuditTrailPanel from "@/components/AuditTrailPanel";
import ClientOverviewTab from "@/components/Client/ClientOverviewTab";
import ReceivablesTiles from "@/components/Client/ReceivablesTiles";
import ClientProjectsTab from "@/components/Client/ClientProjectsTab";
import ClientContactsTab from "@/components/Client/ClientContactsTab";
import ClientBrandsTab from "@/components/Client/ClientBrandsTab";
import ClientInteractionsTab from "@/components/Client/ClientInteractionsTab";
import ActivityTab from "@/components/ActivityTab";
import AttachmentsTab from "@/components/AttachmentsTab";
import EmailsTab from "@/components/EmailsTab";
import ClientTimeline from "@/components/Client/ClientTimeline";
import LogModule from "@/components/LogModule";
import TeamRosterTab from "@/components/TeamRosterTab";
import { EntityDiagramsTab } from "@/components/diagrams/EntityDiagramsTab";
import { EntityDocumentsTab } from "@/components/documents/EntityDocumentsTab";
import { useAuth } from "@/lib/auth";
import { Mail, Clock, AlertTriangle, GitFork, FileText } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

export default function ClientDetail() {
  const { cid } = useParams();
  const { activeWorkspaceId } = useAuth();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [contactsCount, setContactsCount] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  const load = async () => {
    const [c, p] = await Promise.all([
      api.get(`/clients/${cid}`),
      api.get(`/clients/${cid}/projects`),
    ]);
    setClient(c.data);
    setProjects(p.data);
  };

  useEffect(() => { load(); }, [cid]);
  useEffect(() => {
    api.get(`/clients/${cid}/contacts`).then((r) => setContactsCount((r.data || []).length)).catch(() => setContactsCount(0));
  }, [cid]);

  const saveName = async () => {
    const next = (nameDraft || "").trim();
    if (!next) { toast.error("Name cannot be empty"); return; }
    if (next === client?.name) { setEditingName(false); return; }
    try {
      await api.patch(`/clients/${cid}`, { name: next });
      toast.success("Client name updated");
      setEditingName(false);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Rename failed")); }
  };

  const eyebrowBits = [
    client?.client_code && `Code: ${client.client_code}`,
    "Client",
  ].filter(Boolean);

  return (
    <>
      <div className="px-4 sm:px-6 md:px-10 pt-6">
        <Link to="/app/clients" className="text-sm label-eyebrow inline-flex items-center hover:text-primary" data-testid="client-back-link">
          <ArrowLeft className="w-3 h-3 mr-1" /> Back to clients
        </Link>
      </div>
      <PageHeader
        eyebrow={eyebrowBits.join(" · ")}
        title={
          editingName ? (
            <div className="flex items-center gap-2" data-testid="client-name-edit">
              <input
                autoFocus
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
                className="text-4xl font-bold tracking-tight bg-transparent border-b border-primary focus:outline-none min-w-[16rem]"
                data-testid="client-name-input"
              />
              <button type="button" onClick={saveName} className="p-1.5 text-emerald-600 hover:text-emerald-700" data-testid="client-name-save">
                <Check className="w-5 h-5" />
              </button>
              <button type="button" onClick={() => setEditingName(false)} className="p-1.5 text-muted-foreground hover:text-foreground" data-testid="client-name-cancel">
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <span className="inline-flex items-center gap-2 group">
              {client?.name || "—"}
              {client && (
                <button
                  type="button"
                  onClick={() => { setNameDraft(client.name || ""); setEditingName(true); }}
                  className="p-1 text-muted-foreground hover:text-foreground opacity-60 group-hover:opacity-100 transition"
                  title="Rename client"
                  data-testid="client-name-edit-btn"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}
            </span>
          )
        }
        description={[client?.industry, client?.company_size && `${client.company_size} employees`, client?.website].filter(Boolean).join(" · ") || "Overview, projects, contacts, activity, and attachments."}
        testid="client-detail-header"
        actions={
          <div className="flex items-center gap-2">
            <AuditTrailPanel collection="clients" recordId={cid} testid="client-audit-trail" />
            <ClientPortalDialog clientId={cid} />
          </div>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 py-6">
        {/* Feb 2026 R293za · Receivables rollup — billed / received /
            outstanding / overdue tiles pulled from gst_documents by
            customer name. Hidden on clients with no money history. */}
        {client?.name && (
          <div className="mb-5" data-testid="client-receivables-wrap">
            <ReceivablesTiles ws={activeWorkspaceId} customerName={client.name} />
          </div>
        )}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="rounded-none">
            <TabsTrigger value="overview" className="rounded-none" data-testid="client-tab-overview">
              <Building2 className="w-3.5 h-3.5 mr-1.5" /> Overview
            </TabsTrigger>
            <TabsTrigger value="projects" className="rounded-none" data-testid="client-tab-projects">
              <FolderKanban className="w-3.5 h-3.5 mr-1.5" /> Projects · {projects.length}
            </TabsTrigger>
            <TabsTrigger value="brands" className="rounded-none" data-testid="client-tab-brands">
              <Gem className="w-3.5 h-3.5 mr-1.5" /> Brands
            </TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-none" data-testid="client-tab-contacts">
              <Users className="w-3.5 h-3.5 mr-1.5" /> Contacts{contactsCount != null ? ` · ${contactsCount}` : ""}
            </TabsTrigger>
            <TabsTrigger value="team" className="rounded-none" data-testid="client-tab-team">
              <Users className="w-3.5 h-3.5 mr-1.5" /> Team
            </TabsTrigger>
            <TabsTrigger value="activity" className="rounded-none" data-testid="client-tab-activity">
              <Activity className="w-3.5 h-3.5 mr-1.5" /> Activity
            </TabsTrigger>
            <TabsTrigger value="emails" className="rounded-none" data-testid="client-tab-emails">
              <Mail className="w-3.5 h-3.5 mr-1.5" /> Emails
            </TabsTrigger>
            <TabsTrigger value="timeline" className="rounded-none" data-testid="client-tab-timeline">
              <Clock className="w-3.5 h-3.5 mr-1.5" /> Timeline
            </TabsTrigger>
            <TabsTrigger value="interactions" className="rounded-none" data-testid="client-tab-interactions">
              <MessagesSquare className="w-3.5 h-3.5 mr-1.5" /> Interactions
            </TabsTrigger>
            <TabsTrigger value="log" className="rounded-none" data-testid="client-tab-log">
              <AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Log
            </TabsTrigger>
            <TabsTrigger value="attachments" className="rounded-none" data-testid="client-tab-attachments">
              <Paperclip className="w-3.5 h-3.5 mr-1.5" /> Attachments
            </TabsTrigger>
            <TabsTrigger value="diagrams" className="rounded-none" data-testid="client-tab-diagrams">
              <GitFork className="w-3.5 h-3.5 mr-1.5" /> Diagrams
            </TabsTrigger>
            <TabsTrigger value="documents" className="rounded-none" data-testid="client-tab-documents">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Documents
            </TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="pt-6">
            {client && <ClientOverviewTab client={client} onSaved={load} />}
          </TabsContent>
          <TabsContent value="projects" className="pt-6">
            <ClientProjectsTab clientId={cid} projects={projects} onCreated={load} />
          </TabsContent>
          <TabsContent value="brands" className="pt-6">
            <ClientBrandsTab clientId={cid} />
          </TabsContent>
          <TabsContent value="contacts" className="pt-6">
            <ClientContactsTab clientId={cid} projects={projects} />
          </TabsContent>
          <TabsContent value="team" className="pt-6">
            <TeamRosterTab scope="client" wsId={activeWorkspaceId} recordId={cid} />
          </TabsContent>
          <TabsContent value="activity" className="pt-6">
            <ActivityTab entityType="client" entityId={cid} testid="client-activity" />
          </TabsContent>
          <TabsContent value="emails" className="pt-6">
            <EmailsTab entityType="client" entityId={cid} embedded />
          </TabsContent>
          <TabsContent value="timeline" className="pt-6">
            <ClientTimeline wsId={activeWorkspaceId} clientId={cid} />
          </TabsContent>
          <TabsContent value="interactions" className="pt-6">
            <ClientInteractionsTab clientId={cid} wsId={activeWorkspaceId} />
          </TabsContent>
          <TabsContent value="log" className="pt-6">
            <LogModule scope="client" scopeId={cid} defaults={{ client_id: cid }} testid="client-log" />
          </TabsContent>
          <TabsContent value="attachments" className="pt-6">
            <AttachmentsTab scopeType="client" scopeId={cid} testid="client-attachments" />
          </TabsContent>
          <TabsContent value="diagrams" className="pt-6">
            <EntityDiagramsTab entityType="client" entityId={cid} entityName={client?.name || "this client"} />
          </TabsContent>
          <TabsContent value="documents" className="pt-6">
            <EntityDocumentsTab entityType="client" entityId={cid} entityName={client?.name || "this client"} />
          </TabsContent>
        </Tabs>
        {client && <AuditFooter record={client} testid="client-audit-footer" />}
      </div>
    </>
  );
}
